import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CarCard from "../components/CarCard";
import { carsApi } from "../lib/api";
import { Search, X, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function Stock() {
  const { t } = useI18n();
  const [cars, setCars] = useState([]);
  const [allCars, setAllCars] = useState([]); // unfiltered, used to derive brand list
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("Alle");
  const [fuel, setFuel] = useState("Alle");
  const [body, setBody] = useState("Alle");
  const [sort, setSort] = useState("newest");
  const [reloadKey, setReloadKey] = useState(0);

  // Load the full (unfiltered) stock once so the brand dropdown only lists
  // brands that are actually present. Refreshes on manual reload only.
  useEffect(() => {
    let cancelled = false;
    carsApi
      .list({})
      .then((l) => {
        if (!cancelled) setAllCars(l);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Retry helper: try up to 3 times with 800ms backoff. Handles transient
    // network blips on mobile / spotty connections without leaving the page blank.
    const fetchWithRetry = async (attempts = 3) => {
      let lastErr;
      for (let i = 0; i < attempts; i++) {
        try {
          return await carsApi.list({ brand, fuel, body, search, sort, light: 1 });
        } catch (e) {
          lastErr = e;
          if (i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 800 * (i + 1)));
          }
        }
      }
      throw lastErr;
    };

    fetchWithRetry()
      .then((l) => {
        if (cancelled) return;
        setCars(l);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [brand, fuel, body, search, sort, reloadKey]);

  const brandCounts = useMemo(() => {
    const c = { Alle: allCars.length };
    allCars.forEach((x) => (c[x.brand] = (c[x.brand] || 0) + 1));
    return c;
  }, [allCars]);

  // Brand options for the dropdown: only brands that appear in the current
  // unfiltered stock, alphabetised, with "Alle" pinned first. New brands
  // (e.g. Ferrari) show up automatically the moment a car is added; sold-out
  // brands disappear automatically when the last car of that make leaves
  // stock. If the user has a brand selected that no longer exists (e.g. they
  // had filtered by Ferrari and the last one was just sold), we still include
  // it so the <select value> remains valid.
  const brandOptions = useMemo(() => {
    const present = new Set(allCars.map((c) => c.brand).filter(Boolean));
    if (brand && brand !== "Alle") present.add(brand);
    return ["Alle", ...Array.from(present).sort((a, b) => a.localeCompare(b, "nl"))];
  }, [allCars, brand]);

  // Fuel & body filters: same logic — only show values that actually appear in
  // stock, alphabetised, "Alle" pinned first. Keeps the user's current
  // selection valid even if it just became empty.
  const fuelOptions = useMemo(() => {
    const present = new Set(allCars.map((c) => c.fuel).filter(Boolean));
    if (fuel && fuel !== "Alle") present.add(fuel);
    return ["Alle", ...Array.from(present).sort((a, b) => a.localeCompare(b, "nl"))];
  }, [allCars, fuel]);

  const bodyOptions = useMemo(() => {
    const present = new Set(allCars.map((c) => c.body).filter(Boolean));
    if (body && body !== "Alle") present.add(body);
    return ["Alle", ...Array.from(present).sort((a, b) => a.localeCompare(b, "nl"))];
  }, [allCars, body]);

  return (
    <div className="bg-[#1a1a1a] text-white min-h-screen">
      <Navbar />

      <section className="pt-[88px] pb-3 px-5 lg:px-8">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-xs text-white/50 mb-1.5">
            <Link to="/" className="hover:text-[#c5a76f]">{t("common.home")}</Link>
            <span className="mx-2">—</span>
            <span className="text-[#c5a76f]">{t("nav.offer")}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Familjen Grotesk', serif" }}>
            {t("stock.title")} <span className="text-[#c5a76f]">{t("stock.titleAccent")}</span>
          </h1>
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-6">
        <div className="max-w-[1400px] mx-auto">
          {/* Filter dropdowns (compact) */}
          <div className="relative bg-[#242424] border border-white/10 rounded-md p-2.5 lg:p-3">
            {(brand !== "Alle" || fuel !== "Alle" || body !== "Alle" || search) && (
              <button
                type="button"
                onClick={() => {
                  setBrand("Alle");
                  setFuel("Alle");
                  setBody("Alle");
                  setSearch("");
                }}
                data-testid="stock-reset-filters"
                title={t("stock.resetFilters")}
                aria-label={t("stock.resetFilters")}
                className="absolute -top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-[#1a1a1a] border border-white/15 hover:border-[#c5a76f] text-[10px] tracking-wider uppercase text-white/55 hover:text-[#c5a76f] rounded-full transition-colors"
              >
                <RotateCcw className="w-2.5 h-2.5" />
                <span>{t("stock.reset")}</span>
              </button>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <FilterSelect value={brand} onChange={setBrand} label={t("stock.brand")} testid="stock-filter-brand">
                {brandOptions.map((b) => (
                  <option key={b} value={b}>
                    {b === "Alle" ? t("stock.all") : b}
                    {brandCounts[b] ? ` (${brandCounts[b]})` : ""}
                  </option>
                ))}
              </FilterSelect>
              <FilterSelect value={fuel} onChange={setFuel} label={t("stock.fuel")} testid="stock-filter-fuel">
                {fuelOptions.map((f) => (
                  <option key={f} value={f}>
                    {f === "Alle" ? t("stock.all") : f}
                  </option>
                ))}
              </FilterSelect>
              <FilterSelect value={body} onChange={setBody} label={t("stock.body")} testid="stock-filter-body">
                {bodyOptions.map((b) => (
                  <option key={b} value={b}>
                    {b === "Alle" ? t("stock.all") : b}
                  </option>
                ))}
              </FilterSelect>
            </div>
          </div>

          {/* Search bar — prominent, dark luxury style */}
          <div className="relative mt-3">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#c5a76f]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("stock.keyword")}
              data-testid="stock-search-input"
              className="w-full pl-14 pr-12 py-2.5 bg-[#242424] border border-white/10 hover:border-[#c5a76f]/40 focus:border-[#c5a76f] rounded-md text-white text-sm placeholder:text-white/40 focus:outline-none transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/5 hover:bg-[#c5a76f] hover:text-black text-white/60 flex items-center justify-center transition-colors"
                aria-label="Clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto flex flex-wrap items-center justify-between mt-4 mb-4 gap-3">
          <div className="text-[#c5a76f] text-lg font-bold">
            {cars.length} <span className="text-white font-normal text-sm">{t("stock.results")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-white/60 text-xs">{t("stock.sortBy")}</span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="stock-sort-select"
              className="bg-[#242424] text-white px-3 py-1.5 rounded-md text-xs border border-white/10 hover:border-[#c5a76f]/40 focus:border-[#c5a76f] focus:outline-none transition-colors cursor-pointer"
            >
              <option value="newest">{t("stock.sortNewest")}</option>
              <option value="price-asc">{t("stock.sortPriceAsc")}</option>
              <option value="price-desc">{t("stock.sortPriceDesc")}</option>
              <option value="year-desc">{t("stock.sortYearDesc")}</option>
              <option value="km-asc">{t("stock.sortKmAsc")}</option>
            </select>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {loading ? (
            <div className="col-span-full py-20 flex flex-col items-center justify-center text-white/50" data-testid="stock-loading">
              <Loader2 className="w-8 h-8 mb-3 animate-spin text-[#c5a76f]" />
              <span>{t("common.loading")}</span>
            </div>
          ) : error ? (
            <div className="col-span-full py-20 flex flex-col items-center justify-center text-center" data-testid="stock-error">
              <AlertTriangle className="w-10 h-10 mb-3 text-[#c5a76f]" />
              <p className="text-white/80 mb-1">{t("stock.loadFailedTitle")}</p>
              <p className="text-white/40 text-sm mb-5">{t("stock.loadFailedDesc")}</p>
              <button
                onClick={() => setReloadKey((k) => k + 1)}
                data-testid="stock-retry-button"
                className="px-6 py-2.5 bg-[#c5a76f] text-black hover:bg-[#d8bd86] text-sm font-semibold tracking-widest uppercase rounded transition-colors"
              >
                {t("stock.retry")}
              </button>
            </div>
          ) : cars.length === 0 ? (
            <div className="col-span-full py-20 text-center text-white/60">{t("stock.noResults")}</div>
          ) : (
            cars.map((c) => <CarCard key={c.id} car={c} />)
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FilterSelect({ value, onChange, label, testid, children }) {
  return (
    <label className="block">
      <span className="block text-[#c5a76f] text-[9px] uppercase tracking-[0.18em] mb-1">
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full h-[38px] bg-[#1a1a1a] border border-white/10 hover:border-[#c5a76f]/40 focus:border-[#c5a76f] focus:outline-none rounded-md px-3 text-white text-xs transition-colors cursor-pointer"
      >
        {children}
      </select>
    </label>
  );
}
